import React, { useState } from 'react';
import { PlusIcon, AlertCircleIcon } from 'lucide-react';
import { ChatbotCard } from './ChatbotCard';
import { CreateChatbotModal } from './CreateChatbotModal';
import { useUsageTracking } from '../../../hooks/useUsageTracking';
import { toast } from 'sonner';
import { QuotaWarning } from '../../common/QuotaWarning';
interface Props {
  spaceId: string;
}
export const ChatbotsTab = ({
  spaceId
}: Props) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const {
    chatbots: chatbotQuota,
    checkQuota,
    refreshUsage
  } = useUsageTracking();
  const handleError = (error: Error) => {
    toast.error('Operation failed', {
      description: error.message
    });
  };
  const handleCreateChatbot = async (data: any) => {
    // Check if we can create a new chatbot
    if (!checkQuota('chatbots', 1)) {
      toast.error('Chatbot limit reached', {
        description: `Maximum limit of ${chatbotQuota.limit} chatbots reached. Please contact support to increase your limit.`
      });
      return;
    }
    try {
      setIsLoading(true);
      // Here you would typically make an API call to create the chatbot
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call
      await refreshUsage(); // Refresh usage data after creation
      setShowCreateModal(false);
      toast.success('Chatbot created successfully');
    } catch (error) {
      handleError(error as Error);
    } finally {
      setIsLoading(false);
    }
  };
  const QuotaWarning = () => {
    if (chatbotQuota.percentageUsed >= 90) {
      return <div className="bg-status-error/10 border border-status-error/30 rounded-none p-4 flex items-start gap-3 animate-in slide-in-from-top-2" data-id="element-913">
          <AlertCircleIcon size={20} className="text-status-error shrink-0 mt-0.5" data-id="element-914" />
          <div data-id="element-915">
            <p className="text-status-error font-medium" data-id="element-916">
              Chatbot Quota Warning
            </p>
            <p className="text-sm text-status-error/80 mt-1" data-id="element-917">
              You have used {chatbotQuota.current} of {chatbotQuota.limit}{' '}
              available chatbots. Contact support to increase your limit.
            </p>
          </div>
        </div>;
    }
    return null;
  };
  // Mock chatbot data
  const chatbots = [{
    id: '1',
    name: 'Research Assistant',
    personality: 'Helpful research assistant focused on analyzing ADHD studies and papers',
    status: 'active' as const,
    createdAt: new Date().toISOString()
  }, {
    id: '2',
    name: 'Patient Support',
    personality: 'Empathetic support chatbot for answering patient questions about ADHD',
    status: 'active' as const,
    createdAt: new Date().toISOString()
  }];
  return <div className="space-y-8" data-id="element-918">
      <QuotaWarning quotaType="chatbots" quota={chatbotQuota} data-id="element-919" />
      <div className="flex items-start justify-between" data-id="element-920">
        <div data-id="element-921">
          <h2 className="text-xl font-medium text-white font-mono" data-id="element-922">Chatbots</h2>
          <p className="text-sm text-gray-400 mt-1" data-id="element-923">
            {chatbotQuota.current} / {chatbotQuota.limit} Chatbots Used
          </p>
        </div>
        <button onClick={() => setShowCreateModal(true)} disabled={chatbotQuota.isExceeded || isLoading} className="flex items-center gap-2 px-4 py-2 bg-accent hover:bg-accent-light text-white 
                   rounded-none transition-colors font-mono text-sm uppercase tracking-wide
                   disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-924">
          <PlusIcon size={16} data-id="element-925" />
          <span data-id="element-926">New Chatbot</span>
        </button>
      </div>
      <div className="grid grid-cols-1 gap-6" data-id="element-927">
        {chatbots.map(chatbot => <ChatbotCard key={chatbot.id} chatbot={chatbot} onError={handleError} data-id="element-928" />)}
      </div>
      <CreateChatbotModal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} onSave={handleCreateChatbot} isLoading={isLoading} data-id="element-929" />
    </div>;
};